/**
 * Product builder cart/quote property and snapshot helpers.
 * Load after productBuilderUtils and productBuilderMedia, before express-product-builder.js.
 */
(() => {
  window.Express = window.Express || {};

  const { cloneJson } = window.Express.productBuilderUtils;
  const { getOptionVisualImage, mediaToOptionImage } = window.Express.productBuilderMedia;

  const CONFIG_NOTES_DEFAULT = 'Include GST';

  function getOptionIdentity(option = {}) {
    return [option.id, option.key]
      .map((value) => String(value || '').trim())
      .filter(Boolean);
  }

  function hasMatchingParentOption(componentSet = {}, option = {}) {
    const parentIdentities = getOptionIdentity(componentSet.parentOption || {});
    if (!parentIdentities.length) return false;

    const optionIdentities = getOptionIdentity(option);
    return optionIdentities.some((identity) => parentIdentities.includes(identity));
  }

  function normalizeComponentQuantity(value) {
    const quantity = Number(value || 1);
    return Number.isFinite(quantity) && quantity > 0 ? Math.floor(quantity) : 1;
  }

  function getSelectedOptionComponentSet(option = {}) {
    return (Array.isArray(option.optionComponentSets) ? option.optionComponentSets : [])
      .find((componentSet) => hasMatchingParentOption(componentSet, option) && Array.isArray(componentSet.lines)) || null;
  }

  function getSelectedOptionComponentLines(option = {}) {
    const componentSet = getSelectedOptionComponentSet(option);

    return (componentSet?.lines || [])
      .filter((line) => line.option)
      .map((line) => ({
        ...line,
        quantity: normalizeComponentQuantity(line.quantity),
      }));
  }

  function getOptionPriceDeltaCents(option = {}) {
    const lines = getSelectedOptionComponentLines(option);

    if (!lines.length) return Number(option.priceDeltaCents || 0);

    return lines.reduce((sum, line) => {
      return sum + (Number(line.option?.priceDeltaCents || 0) * line.quantity);
    }, 0);
  }

  function buildOptionProductPayload(option = {}) {
    const lines = getSelectedOptionComponentLines(option);
    const payload = {
      id: option.id || option.key,
      value: option.label,
      internal_code: option.internalCode || '',
      sku: option.sku || '',
      group_key: option.groupKey,
    };

    if (lines.length) {
      payload.components = lines.map((line) => {
        const componentOption = line.option || {};
        return {
          id: componentOption.id || componentOption.key,
          value: line.displayName || componentOption.label || option.label,
          internal_code: componentOption.internalCode || '',
          sku: componentOption.sku || '',
          quantity: line.quantity,
        };
      });
    }

    return payload;
  }

  function getSelectedOptionsTotal(config, selectedOptions) {
    return Number(config?.product?.basePriceCents || 0)
      + (selectedOptions || []).reduce((sum, option) => sum + getOptionPriceDeltaCents(option), 0);
  }

  function buildProperties(config, options, total) {
    const properties = {
      _price: total / 100,
      _quote_source: 'product_builder',
      _builder_mode: config.quote?.mode || 'cart_quote',
      _builder_version: config.version || '1.0',
    };
    const parentSku = String(config.product?.sku || '').trim();
    if (parentSku) properties._parent_sku = parentSku;

    const summaryNote = config.ui?.summaryNote || config.notes || CONFIG_NOTES_DEFAULT;
    const quoteOptions = {
      currency: config.product?.currencyCode || 'AUD',
      gstIncludedCopy: /gst/i.test(String(summaryNote || '')) && /include/i.test(String(summaryNote || '')) ? summaryNote : '',
      items: options.map((option) => {
        const priceCents = getOptionPriceDeltaCents(option);

        return {
          title: `${option.groupLabel}: ${option.label}`,
          image: getOptionVisualImage(option),
          priceCents,
          included: priceCents === 0,
        };
      }),
      basePrice: {
        title: 'Base Price',
        image: mediaToOptionImage(config.product?.product_image || config.product?.images?.[0], config.product?.title || 'Base Price'),
        priceCents: Number(config.product?.basePriceCents || 0),
        included: false,
      },
    };

    properties._quote_options = quoteOptions;

    options.forEach((option) => {
      properties[`builder_${option.groupKey}`] = option.label;
      properties[`option_product-${option.groupLabel}`] = buildOptionProductPayload(option);
    });

    return properties;
  }

  function createBuilderSnapshot(config, selections) {
    return {
      type: 'product_builder',
      config: cloneJson(config),
      selections: cloneJson(selections) || {},
    };
  }

  function createSelectedOptionsSnapshot(config, selections, selectedOptions, total) {
    return {
      type: 'product_builder',
      config: cloneJson(config),
      selections: cloneJson(selections) || {},
      selectedOptions: (selectedOptions || []).map((option) => ({ ...option })),
      total,
      currency: config?.product?.currencyCode || 'AUD',
    };
  }

  window.Express.productBuilderProperties = {
    buildProperties,
    createBuilderSnapshot,
    createSelectedOptionsSnapshot,
    getOptionPriceDeltaCents,
    getSelectedOptionComponentLines,
    getSelectedOptionComponentSet,
    getSelectedOptionsTotal,
  };
})();
